import { PlexUserRelationState } from '@all-hands-hq/plex-graphql-schema';
import { DataProxy } from '@apollo/client';
import { FetchResult } from 'apollo-link';
import React from 'react';

import {
  CreatePlexMutation,
  FullPlexFragment,
  PlexInput,
  PlexMemberRole,
  useGetLoggedInUserQuery,
} from '../../../../graphql/gen/types';
import { useCreatePlexMutation } from '../../../../graphql/mutations/CreatePlex';
import { prependPlexToDomainPlexesCache } from '../../../../graphql/updates/prependPlexToDomainPlexesCache';
import { prependPlexToUserPlexesCache } from '../../../../graphql/updates/prependPlexToUserPlexesCache';
import { selectOnlyOnePlexFilterItem } from '../../../../models/filters/actions/selectOnlyOnePlexFilterItem';
import { useFiltersState } from '../../../../models/filters/useFiltersState';
import { logAnalytics } from '../../shared/logAnalytics';
import { useDialogState } from '../useDialogState';
import { PlexDialog } from './BasePlexDialog';

export function CreatePlexDialog(): React.ReactElement | null {
  const { data } = useGetLoggedInUserQuery({ variables: {} });
  const [, setDialogState] = useDialogState();
  const [, dispatch] = useFiltersState();

  const [createPlexMutation, { data: saveData, loading }] = useCreatePlexMutation({
    update: (proxy: DataProxy, { data }: FetchResult<CreatePlexMutation>): void => {
      // Update cache on success
      const newPlex = data?.createPlex;
      if (newPlex?.__typename === 'Plex') {
        prependPlexToUserPlexesCache(proxy, { plex: newPlex });
        prependPlexToDomainPlexesCache(proxy, {
          plex: {
            ...newPlex,
            summary: {
              id: newPlex.id,
              members: newPlex.settings.members.map((m) => ({
                name: m.user.__typename === 'PlexRegisteredUser' ? m.user.name : m.user.email,
                role: m.role,
                picture: m.user.__typename === 'PlexRegisteredUser' ? m.user.picture : '',
              })),
              channelsCount: 0,
              postsCount: 0,
              faqsCount: 0,
              events: [],
            },
          },
        });
      }
    },
  });

  if (data?.currentUser == null) {
    return null;
  }
  const { id, name, email, picture } = data.currentUser;

  const defaultPlex: Pick<
    FullPlexFragment,
    'name' | 'description' | 'settings' | 'currentUserRelation' | 'googleCalendars'
  > = {
    name: '',
    description: '',
    currentUserRelation: {
      id: '',
      state: PlexUserRelationState.Owner,
      role: PlexMemberRole.Owner,
    },
    googleCalendars: [],
    settings: {
      members: [
        {
          user: {
            __typename: 'PlexRegisteredUser',
            id,
            email,
            name,
            picture,
          },
          role: PlexMemberRole.Owner,
        },
      ],
    },
  };

  const createPlex = (plex: PlexInput): void => {
    createPlexMutation({
      variables: { plex },
    })
      .then(({ data }) => {
        if (data?.createPlex?.__typename === 'Plex') {
          setDialogState(null, 'replace');
          dispatch(selectOnlyOnePlexFilterItem({ permalink: data.createPlex.permalink, selected: true }));
          logAnalytics({ name: 'created plex', details: { plexId: data?.createPlex?.id } });
        }
      })
      // Need this to avoid having an exception being thrown in dev
      .catch((_) => {
        return undefined;
      });
  };

  return (
    <PlexDialog
      plex={defaultPlex}
      mode="create"
      onSave={createPlex}
      saveLoading={loading}
      saveError={saveData?.createPlex.__typename === 'PlexInputError' ? saveData.createPlex : null}
    />
  );
}
